import React, { forwardRef, useImperativeHandle, useRef, useEffect } from "react"
import { flattenStyle } from "./style"
import { resolveImageSrc, type ImageSrcInput } from "./image-source"

export const ResizeMode = {
  CONTAIN: "contain",
  COVER: "cover",
  STRETCH: "fill",
  STRETCH_LEGACY: "stretch",
}

export type AVPlaybackStatus = {
  isLoaded?: boolean
  durationMillis?: number
  positionMillis?: number
  isPlaying?: boolean
  didJustFinish?: boolean
}

export type VideoProps = {
  src?: ImageSrcInput
  /** @deprecated 请使用 src */
  source?: ImageSrcInput
  style?: unknown
  resizeMode?: string
  shouldPlay?: boolean
  useNativeControls?: boolean
  isLooping?: boolean
  usePoster?: boolean
  posterSrc?: ImageSrcInput
  /** @deprecated 请使用 posterSrc */
  posterSource?: ImageSrcInput
  posterStyle?: unknown
  onLoad?: (size?: { width: number; height: number }) => void
  onPlaybackStatusUpdate?: (status: AVPlaybackStatus) => void
  [key: string]: unknown
}

export type VideoRef = {
  playAsync: () => Promise<void>
  stopAsync: () => Promise<void>
  setPositionAsync: (millis: number) => Promise<void>
  pauseAsync: () => Promise<void>
}

function resolveVideoSrc(src?: ImageSrcInput, legacySource?: ImageSrcInput): string | undefined {
  const href = resolveImageSrc(src, legacySource)
  return href || undefined
}

function toObjectFit(resizeMode?: string): React.CSSProperties["objectFit"] {
  if (resizeMode === ResizeMode.COVER) return "cover"
  if (resizeMode === "stretch" || resizeMode === ResizeMode.STRETCH_LEGACY) return "fill"
  return "contain"
}

export const Video = forwardRef<VideoRef, VideoProps>(function Video(
  {
    src,
    source,
    style,
    resizeMode = ResizeMode.CONTAIN,
    shouldPlay,
    useNativeControls = true,
    isLooping,
    posterSrc,
    posterSource,
    usePoster,
    onLoad,
    onPlaybackStatusUpdate,
  },
  ref,
) {
  const videoRef = useRef<HTMLVideoElement>(null)

  const emitStatus = () => {
    const el = videoRef.current
    if (!el || !onPlaybackStatusUpdate) return
    onPlaybackStatusUpdate({
      isLoaded: el.readyState >= 2,
      durationMillis: Number.isFinite(el.duration) ? Math.floor(el.duration * 1000) : 0,
      positionMillis: Math.floor(el.currentTime * 1000),
      isPlaying: !el.paused && !el.ended,
      didJustFinish: el.ended,
    })
  }

  useImperativeHandle(ref, () => ({
    playAsync: async () => {
      await videoRef.current?.play()
    },
    pauseAsync: async () => {
      videoRef.current?.pause()
    },
    stopAsync: async () => {
      const el = videoRef.current
      if (!el) return
      el.pause()
      el.currentTime = 0
    },
    setPositionAsync: async (millis: number) => {
      if (videoRef.current) videoRef.current.currentTime = millis / 1000
    },
  }))

  useEffect(() => {
    if (shouldPlay) void videoRef.current?.play()
  }, [shouldPlay, src, source])

  const href = resolveVideoSrc(src, source)
  const poster = usePoster ? resolveVideoSrc(posterSrc, posterSource) : undefined

  return (
    <video
      ref={videoRef}
      src={href}
      poster={poster}
      controls={useNativeControls}
      loop={isLooping}
      playsInline
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        background: "#000",
        objectFit: toObjectFit(resizeMode),
        ...flattenStyle(style),
      }}
      onLoadedData={() => {
        const el = videoRef.current
        onLoad?.(
          el?.videoWidth && el?.videoHeight
            ? { width: el.videoWidth, height: el.videoHeight }
            : undefined,
        )
        emitStatus()
      }}
      onTimeUpdate={emitStatus}
      onPlay={emitStatus}
      onPause={emitStatus}
      onEnded={emitStatus}
    />
  )
})

export const Audio = {
  Sound: class {
    loadAsync = async () => ({})
    playAsync = async () => ({})
    stopAsync = async () => ({})
    unloadAsync = async () => ({})
    setPositionAsync = async () => ({})
  },
}

export type FileSystemDownloadResult = { uri: string }

export default { Video, Audio, ResizeMode }
